import {TriangleDownIcon} from '@primer/octicons-react'
import type React from 'react'
import {ActionList, ActionMenu, Button, ButtonGroup, IconButton, type ButtonProps} from '@primer/react'

export interface CopilotFixSplitButtonItem<T extends string> {
  value: T
  label: string
  description?: string
}

export interface CopilotFixSplitButtonProps<T extends string> {
  /** The available modes/actions in the dropdown */
  items: Array<CopilotFixSplitButtonItem<T>>
  /** The currently selected mode */
  selectedValue: T
  /** Called when the user picks a different mode from the dropdown */
  onSelectionChange: (value: T) => void
  /** Called when the primary button is clicked */
  onAction: () => void
  /** Visual variant for both buttons */
  variant?: ButtonProps['variant']
  /** Button size */
  size?: ButtonProps['size']
  /** Whether the primary button shows a loading spinner */
  loading?: boolean
  /** Whether the primary button is disabled */
  disabled?: boolean
  /** Aria-label for the dropdown trigger */
  dropdownAriaLabel: string
  /** Ref forwarded to the primary button */
  ref?: React.Ref<HTMLButtonElement>
}

export function CopilotFixSplitButton<T extends string>({
  items,
  selectedValue,
  onSelectionChange,
  onAction,
  variant = 'default',
  size,
  loading,
  disabled,
  dropdownAriaLabel,
  ref,
}: CopilotFixSplitButtonProps<T>) {
  const selectedItem = items.find(item => item.value === selectedValue) ?? items[0]
  if (!selectedItem) {
    return null
  }

  return (
    <ButtonGroup>
      {/* Primer's loading prop hides children (via visibility:hidden) when there's no
         leadingVisual, replacing them with a spinner in the text grid area. Providing a
         dummy leadingVisual forces the spinner into the icon slot so the label stays visible. */}
      <Button
        className="flex-1"
        ref={ref}
        size={size}
        variant={variant}
        onClick={onAction}
        disabled={disabled && !loading}
        loading={loading}
        loadingAnnouncement="Fixing…"
        leadingVisual={() => <span />}
      >
        {loading ? 'Fixing…' : selectedItem.label}
      </Button>
      <ActionMenu>
        <ActionMenu.Anchor>
          <IconButton
            /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
            className="flex-0"
            size={size}
            variant={variant}
            icon={TriangleDownIcon}
            aria-label={dropdownAriaLabel}
            disabled={disabled || loading}
          />
        </ActionMenu.Anchor>
        <ActionMenu.Overlay align="end" width="medium">
          <ActionList selectionVariant="single">
            {items.map(item => (
              <ActionList.Item
                key={item.value}
                selected={selectedValue === item.value}
                onSelect={() => onSelectionChange(item.value)}
              >
                {item.label}
                {item.description != null && (
                  <ActionList.Description variant="block">{item.description}</ActionList.Description>
                )}
              </ActionList.Item>
            ))}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </ButtonGroup>
  )
}
